// 유료 기능(세금계산서 발행) 카드 게이트 — 체험판 정책 (2026-07-23)
// 거래명세서 작성·발행·프린트·다운로드 = 체험 무료 / 세금계산서 발행 = 결제 카드 등록 필수
// 2026-10-04 요금 변경(계획 W6): 요금 판정표(billingPolicy) 반영 — 기존 기공소는 2026-12-31까지 카드 없이 통과,
//   요금표 있는 신규는 체험 기간 중 통과, 면제·기존 치과도 통과. 그 외(구독 대상 아닌 유형 포함)는 현행대로 카드·체험 확인.
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFeatures, isBetaUser, paywallActive } from './features';
import { loadBillingDecision, isFreeNow, decisionHasTariff, isLiveSub, resolveBillingOwner } from './billingPolicy';

// 카드 없이 통과시키는 판정 사유 — 구독 대상 아닌 유형(not_subject 등)은 여기 넣지 않는다(전자발행 실비 게이트 유지)
const FREE_PASS_REASONS = ['all_free', 'exempt', 'legacy_clinic', 'clinic_free', 'legacy_lab', 'tariff'];   // clinic_free: 2026-10-30 치과 무료(billingPolicy)

export async function hasActiveCard(user) {
    if (!user?.uid) return false;
    // 판정 대상 = 본인 요금표가 있으면 본인, 없으면 소속 업체(companyId) — 서버 resolveOwner 와 같은 규칙 (보안 P1-1)
    const resolved = await resolveBillingOwner(user);
    const uid = resolved.ownerUid;
    if (!uid) return false;
    // 설정(config/features) 읽기 실패 = 통과 + 기록 (fail-open, 앱 lib/core/utils/card_gate.dart 와 같은 규칙 — 2026-10-04)
    //   설정을 못 읽었다고 유료 고객·무료기간 고객의 세금계산서 발행을 막지 않는다. 실제 발행 비용은 서버가 다시 판정한다.
    //   getFeatures 는 실패해도 throw 하지 않고 기본값 + _readFailed 를 돌려주므로 그 표시로 판단한다(기본값의 빈 베타 목록으로 베타가 막히지 않게).
    let features;
    try {
        features = await getFeatures();
    } catch (e) {
        console.error('[cardGate] 설정(config/features) 읽기 실패 — 통과 처리(fail-open):', e?.message);
        return true;
    }
    if (features?._readFailed === true) {
        console.error('[cardGate] 설정(config/features) 읽기 실패 표시(_readFailed) — 통과 처리(fail-open)');
        return true;
    }
    // 베타(아이반+연결 치과)는 게이트 면제 — 파일럿 검증 경로 유지
    try {
        // 요금 잠금 미적용(allFree=true) — 세금계산서 발행 카드 게이트도 해제 (W9: paywallActive 한 곳)
        if (!paywallActive(features)) return true;
        // 요금 판정표: 면제·기존 치과는 통과, 기존 기공소(12-31까지)·신규(체험 종료일까지)는 무료기간 안이면 통과
        const decision = await loadBillingDecision(user, { features, resolved });
        if (FREE_PASS_REASONS.includes(decision.reason) && isFreeNow(decision)) return true;
        // 베타 — 판정 순서는 서버와 같게(요금표 → overseas): 요금표가 있으면 해외(labKind) 단축 통과 무시 (보안 P1-1)
        if (isBetaUser(features, user, user?.email, { ignoreOverseas: decisionHasTariff(decision) })) return true;
    } catch (e) {
        console.error('[cardGate] 요금 판정 실패 — 카드 검사로 진행:', e?.message);
    }
    try {
        const snap = await getDoc(doc(db, 'billingKeys', uid));
        if (snap.exists() && snap.data().status === 'active') return true;
    } catch (e) {
        // 카드 조회 실패 — 아래 체험 확인으로 계속 진행 (조용한 실패 금지: 기록)
        console.error('[cardGate] 결제 카드 조회 실패 — 체험 확인으로 진행:', e?.message);
    }
    // (a) 이용권이 살아 있으면 카드 없이 발행 허용 — 판정은 billingPolicy.isLiveSub 한 곳(앱 card_gate.dart 와 통일, 2026-10-04):
    //   trial(종료일 전)·active(만료 후 결제 유예 7일 포함)·cancelled(낸 기간 남음). 종전: 해지 안 된 체험(trialGranted)만 인정.
    try {
        const subs = await getDocs(query(collection(db, 'appSubscriptions'), where('userId', '==', uid)));
        const now = new Date();
        return subs.docs.some(d => isLiveSub(d.data(), now));
    } catch (e) {
        console.error('[cardGate] 체험 구독 조회 실패 — 카드 필요로 처리:', e?.message);
        return false;
    }
}

// 게이트 통과 실패 시 공통 안내 + 구독 화면 이동 유도. true 반환 = 진행 가능
export function confirmCardRequired() {
    const go = window.confirm(
        '세금계산서 발행은 전자발행 비용이 발생하는 유료 기능이라 결제 카드 등록 후 이용할 수 있습니다.\n' +
        '(거래명세서 작성·발행·프린트는 체험 기간 동안 무료입니다)\n\n' +
        '지금 카드를 등록하러 갈까요?'
    );
    if (go) window.location.href = '/subscription';
    return false;
}
